import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentService } from '@galaxy/core';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OrderForm } from '@vendasta/marketplace-apps';
import {
  CustomFieldsInterface,
  FieldInterface,
  FieldType,
  FormAnswersInterface,
  FulfillmentFormConfigInterface,
  FulfillmentFormSubmissionInterface,
  FulfillmentOrderStatus,
  UpsertFulfillmentFormSubmissionRequest,
  UpsertFulfillmentFormSubmissionRequestInterface,
} from '@vendasta/order-fulfillment';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { FieldType as SalesOrdersFieldType } from '@vendasta/sales-orders';
import {
  CustomFieldsAnswer,
  CustomFieldsAnswers,
  OrderFormComponent,
  OrderFormFieldOptionsType,
  OrderFormInterface,
  ProductInfoInterface,
  User,
  VaOrderFormModule,
} from '@vendasta/store';
import { BehaviorSubject, EMPTY, Observable, ReplaySubject, Subscription, combineLatest, of } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { getTemporaryStorageKey } from '../utils';
import { WorkOrderFormConfigService } from '../work-order-form-config.service';
import { WorkOrderPersona } from '../work-order-landing-page/interfaces';
import { WorkOrderModule } from '../work-order.module';
import { FormConfirmDialogComponent } from './form-confirm-dialog/form-confirm-dialog.component';
import { FormConflictDialogComponent } from './form-conflict-dialog/form-conflict-dialog.component';
import { FormErrorDialogComponent } from './form-error-dialog/form-error-dialog.component';
import { WorkOrderFormService } from './work-order-form.service';

@Component({
  selector: 'work-order-form',
  templateUrl: './work-order-form.component.html',
  styleUrls: ['./work-order-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    VaOrderFormModule,
    MatButtonModule,
    GalaxyLoadingSpinnerModule,
    TranslateModule,
    GalaxyButtonLoadingIndicatorModule,
    GalaxyEmptyStateModule,
    WorkOrderModule,
    MatCheckboxModule,
    MatExpansionModule,
  ],
  providers: [WorkOrderFormService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkOrderFormComponent implements OnInit, OnDestroy, AfterViewInit {
  // A list of users that will be displayed for VBC User fields on the forms. Not Required
  @Input() users: User[] = [];

  // The persona this user is using to access
  @Input() set persona(persona: WorkOrderPersona) {
    this.persona$$.next(persona);
  }

  // ID of the sales order the work order belongs to
  @Input() salesOrderId: string;

  // ID of the app the current form is form
  @Input() appId: string;

  // ID of the account group this work order belongs to.
  @Input() accountGroupId: string;

  // marketId of the account
  @Input() marketId: string;

  // status of the work order
  @Input() status: FulfillmentOrderStatus;

  // id of the work order
  @Input() fulfillmentOrderId: string;

  // Boolean to determine if officeEditableOnly and forOfficeUseOnly fields are shown to users
  @Input() showOfficeUseQuestions = false;

  // Fulfillment Order Form, as stored on the Fulfillment Order.
  // This will contain the configuration of the form at the time the FulfillmentOrder was created.
  // Required.
  @Input() fulfillmentOrderForm: OrderForm = new OrderForm();

  // A template with any extra content to be included in the header
  @Input() extraHeaderContent: TemplateRef<any>;

  // the partner id of the reseller (if any)
  @Input() resellerPartnerId: string;

  // Boolean to determine if the form is in a read only state
  @Input() readOnly: boolean;

  // This event will be thrown when the change on the form forces an update on the fulfillment order status
  @Output() fulfillmentOrderStatusChanged = new EventEmitter<FulfillmentOrderStatus>();

  // This event will be emitted when the form is submitted
  @Output() submitClicked: EventEmitter<void> = new EventEmitter();

  @Output() doneLoading: EventEmitter<void> = new EventEmitter();

  form: UntypedFormGroup = new UntypedFormGroup({});
  productInfo$$: ReplaySubject<ProductInfoInterface[]> = new ReplaySubject(1);
  orderForm$$: ReplaySubject<OrderFormInterface[]> = new ReplaySubject(1);
  fileUploadUrl: string;
  @ViewChild('fulfillmentForm') public orderFormComponent: OrderFormComponent;
  answers$: Observable<CustomFieldsAnswers[]>;
  submitLoading$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  saveProgressLoading$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  hasForm$: Observable<boolean>;
  submittedSubject$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  formSubmitted$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  foStatus$$: ReplaySubject<FulfillmentOrderStatus> = new ReplaySubject<FulfillmentOrderStatus>(1);
  persona$$: BehaviorSubject<WorkOrderPersona> = new BehaviorSubject(WorkOrderPersona.CP);
  underReview$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  subscriptions: Subscription[] = [];
  latestSubmission$: Observable<FulfillmentFormSubmissionInterface>;
  private lastViewedTimestamp$$: BehaviorSubject<string> = new BehaviorSubject('0');
  private config$: Observable<FulfillmentFormConfigInterface>;
  WorkOrderPersona = WorkOrderPersona;

  constructor(
    private workOrderFormV2Service: WorkOrderFormService,
    private snackbarService: SnackbarService,
    private environmentService: EnvironmentService,
    private dialog: MatDialog,
    private workOrderFormConfigService: WorkOrderFormConfigService,
    private productAnalyticsService: ProductAnalyticsService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.fileUploadUrl = `https://order-fulfillment-api-${this.environmentService.getEnvironmentString()}.apigateway.co/fulfillment-form/attachments/upload`;
    this.config$ = this.workOrderFormConfigService
      .getFormConfig(this.salesOrderId, this.appId, this.accountGroupId)
      .pipe(
        tap(() => {
          this.foStatus$$.next(this.status);
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
    this.hasForm$ = this.config$.pipe(map((config) => config?.orderForm?.length > 0));

    this.latestSubmission$ = this.workOrderFormConfigService
      .getLatestSubmission(this.salesOrderId, this.appId, this.accountGroupId)
      .pipe(
        take(1),
        tap((latestSubmission) => {
          if (latestSubmission) {
            this.lastViewedTimestamp$$.next(latestSubmission?.version || '0');
          }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.subscriptions.push(
      this.persona$$
        .pipe(
          switchMap((persona) =>
            this.workOrderFormV2Service.getProductInfo(this.appId, this.resellerPartnerId, this.marketId, persona),
          ),
          map((productInfo) => [productInfo]),
          shareReplay({ refCount: true, bufferSize: 1 }),
        )
        .subscribe(this.productInfo$$),
    );

    this.answers$ = combineLatest([this.latestSubmission$, this.productInfo$$]).pipe(
      filter(([submission, productInfo]) => !!submission && !!productInfo),
      map(([submission, productInfo]) =>
        this.getCustomFieldsAnswersFromFulfillmentFormSubmission(submission, productInfo?.[0]),
      ),
      tap(() => (this.form = new UntypedFormGroup({}))),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.subscriptions.push(
      this.config$
        .pipe(
          withLatestFrom(this.productInfo$$),
          map(([config, productInfo]) => {
            const info = productInfo?.[0];
            return [
              {
                appId: info?.productId,
                ...config,
                addonId: info?.addonInfo?.[0]?.addonId,
                customFields: config?.orderForm?.map((cf) => {
                  const res: OrderFormFieldOptionsType = {
                    ...cf,
                    options: cf?.optionsWithLabels,
                  } as OrderFormFieldOptionsType;
                  return res;
                }),
              },
            ];
          }),
          shareReplay({ refCount: true, bufferSize: 1 }),
        )
        .subscribe(this.orderForm$$),
    );

    this.subscriptions.push(
      combineLatest([this.foStatus$$, this.persona$$]).subscribe(([foStatus, persona]) => {
        this.underReview$$.next(
          foStatus === FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_REVIEW && persona === WorkOrderPersona.VENDOR,
        );
      }),
      this.persona$$.subscribe((persona) => {
        this.formSubmitted$$.next(
          Boolean(
            this.status &&
              this.status !== FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED &&
              persona !== WorkOrderPersona.VENDOR,
          ),
        );
      }),
    );
    combineLatest([this.orderForm$$, this.productInfo$$])
      .pipe(take(1))
      .subscribe(() => this.doneLoading.emit());
  }

  ngAfterViewInit(): void {
    // Get the fragment from the url when fragment$ fires and scroll to the appropriate section
    const fragment$: Observable<string> = this.route.fragment;
    this.subscriptions.push(
      fragment$.subscribe((fragment) => {
        if (fragment) {
          const element = document.getElementById(fragment);
          if (element) {
            element.scrollIntoView();
          }
        }
      }),
    );
  }

  private getCustomFieldsAnswersFromFulfillmentFormSubmission(
    submission: FulfillmentFormSubmissionInterface,
    productInfo: ProductInfoInterface,
  ): CustomFieldsAnswers[] {
    const answers: CustomFieldsAnswers[] = [];
    submission?.formAnswers?.forEach((formAnswer: FormAnswersInterface) => {
      const cfAnswers: CustomFieldsAnswer[] = [];
      formAnswer?.customFields?.forEach((cf) => {
        cf?.fields?.forEach((f) => {
          cfAnswers.push({
            answer: f.answer,
            fieldId: f.fieldId,
          });
        });
      });
      let customFieldsAnswersToAdd: CustomFieldsAnswers;
      if (productInfo?.addonInfo) {
        customFieldsAnswersToAdd = {
          productID: productInfo.productId,
          customFieldsAnswers: cfAnswers,
          addonKey: {
            appId: productInfo.productId,
            addonId: productInfo?.addonInfo?.[0]?.addonId,
          },
        };
      } else {
        customFieldsAnswersToAdd = {
          productID: submission.appId,
          customFieldsAnswers: cfAnswers,
        };
      }
      // if formId matches, then it should be first so UI will render it's answers
      formAnswer.formId === this.appId
        ? answers.unshift(customFieldsAnswersToAdd)
        : answers.push(customFieldsAnswersToAdd);
    });
    return answers;
  }

  salesOrderFieldTypeToFieldType(salesOrderFieldType: SalesOrdersFieldType): FieldType {
    switch (salesOrderFieldType) {
      case SalesOrdersFieldType.CHECKBOX:
        return FieldType.FIELD_TYPE_CHECKBOX;
      case SalesOrdersFieldType.DROPDOWN:
        return FieldType.FIELD_TYPE_DROPDOWN;
      case SalesOrdersFieldType.FILE:
        return FieldType.FIELD_TYPE_FILE;
      case SalesOrdersFieldType.TEXT:
        return FieldType.FIELD_TYPE_TEXT;
      case SalesOrdersFieldType.TEXTAREA:
        return FieldType.FIELD_TYPE_TEXT_AREA;
      case SalesOrdersFieldType.VBCUSER:
        return FieldType.FIELD_TYPE_VBCUSER;
    }
    return FieldType.FIELD_TYPE_NOT_SPECIFIED;
  }

  getCustomFieldValues(): CustomFieldsInterface[] {
    const customFields: CustomFieldsInterface[] = this.orderFormComponent.getCustomFormData().map((cf) => {
      return {
        fields: cf.fields.map((field) => {
          return {
            ...field,
            fieldType: this.salesOrderFieldTypeToFieldType(field.fieldType),
          } as FieldInterface;
        }),
      } as CustomFieldsInterface;
    });
    return customFields;
  }

  handleSubmitClicked(): void {
    this.submitClicked.emit();
    this.submitLoading$$.next(true);
    const customFields = this.getCustomFieldValues().filter((cf) => cf?.fields?.length > 0);
    let req: UpsertFulfillmentFormSubmissionRequestInterface;

    if (this.form.valid) {
      this.dialog
        .open(FormConfirmDialogComponent, {})
        .afterClosed()
        .pipe(
          switchMap((result) => {
            if (result) {
              req = {
                salesOrderId: this.salesOrderId,
                appId: this.appId,
                businessId: this.accountGroupId,
                lastViewedTimestamp: this.lastViewedTimestamp$$.getValue(),
                formAnswers: [
                  {
                    customFields: customFields,
                    formId: this.appId,
                  },
                ],
              };
              return this.workOrderFormV2Service
                .updateFormSubmission(new UpsertFulfillmentFormSubmissionRequest(req))
                .pipe(
                  tap(() =>
                    this.fulfillmentOrderStatusChanged.emit(FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_REVIEW),
                  ),
                  take(1),
                  catchError((err) => {
                    if (err.status === 409) {
                      const tempStorageKey = getTemporaryStorageKey(this.accountGroupId, this.appId);
                      if (tempStorageKey) {
                        sessionStorage.removeItem(tempStorageKey);
                      }
                      this.dialog.open(FormConflictDialogComponent, {
                        width: '660px',
                      });
                      return EMPTY;
                    }
                    throw err;
                  }),
                  finalize(() => this.submitLoading$$.next(false)),
                );
            }
            this.submitLoading$$.next(false);
            return EMPTY;
          }),
          switchMap((version) => {
            return of(version);
          }),
        )
        .subscribe(
          (version) => {
            this.snackbarService.openSuccessSnack('FORM_V2.SUBMIT_SUCCESS');
            this.lastViewedTimestamp$$.next(version);

            this.submittedSubject$$.next(true);
            this.formSubmitted$$.next(this.persona$$.getValue() !== WorkOrderPersona.VENDOR);

            // clear the form data from cache
            const tempStorageKey = getTemporaryStorageKey(this.accountGroupId, this.appId);
            if (tempStorageKey) {
              sessionStorage.removeItem(tempStorageKey);
            }
          },
          () => this.snackbarService.openErrorSnack('FORM_V2.SUBMIT_ERROR'),
        );
    } else {
      // mark all as touched to highlight missing required fields
      this.form.markAllAsTouched();
      this.submitLoading$$.next(false);
      this.dialog.open(FormErrorDialogComponent, {});
    }
  }

  private areTouchedFormControlsValid(): boolean {
    const touchedControls: UntypedFormGroup = new UntypedFormGroup({});
    const controls = this.form.controls;
    for (const key in controls) {
      const control = controls[key];
      for (const k in control?.['controls']) {
        const innerControl = control['controls'][k];
        if (innerControl.touched) {
          touchedControls.addControl(k, innerControl);
        }
      }
    }
    return touchedControls.valid;
  }

  handleSaveProgressClicked(): void {
    if (!this.areTouchedFormControlsValid()) {
      this.snackbarService.openErrorSnack('FORM_V2.SAVE_PROGRESS_ERROR_INVALID');
      return;
    }

    this.saveProgressLoading$$.next(true);
    const customFields = this.getCustomFieldValues().filter((cf) => cf?.fields?.length > 0);

    const req: UpsertFulfillmentFormSubmissionRequestInterface = {
      salesOrderId: this.salesOrderId,
      appId: this.appId,
      businessId: this.accountGroupId,
      lastViewedTimestamp: this.lastViewedTimestamp$$.getValue(),
      formAnswers: customFields.map((cf, i) => {
        return {
          customFields: [cf],
          formId: i.toString(),
        } as FormAnswersInterface;
      }),
    };
    this.workOrderFormV2Service
      .updateFormSubmission(new UpsertFulfillmentFormSubmissionRequest(req))
      .pipe(
        take(1),
        finalize(() => this.saveProgressLoading$$.next(false)),
      )
      .subscribe(
        (version) => {
          this.snackbarService.openSuccessSnack('FORM_V2.SAVE_PROGRESS_SUCCESS');
          this.lastViewedTimestamp$$.next(version);

          // clear the form data from cache
          const tempStorageKey = getTemporaryStorageKey(this.accountGroupId, this.appId);
          if (tempStorageKey) {
            sessionStorage.removeItem(tempStorageKey);
          }
        },
        () => this.snackbarService.openErrorSnack('FORM_V2.SAVE_PROGRESS_ERROR'),
      );
  }

  handleSaveChangesClicked = (): void => {
    this.handleSaveProgressClicked();
    this.productAnalyticsService.trackEvent(
      'save-progress-fulfillment-form-button-clicked',
      'work-order',
      'button-clicked',
    );
  };

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
